import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/feature/LanguageSwitcher';

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 30);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const productLinks = [
    { key: 'nav.productSellMore', href: '/sell-more-workshop' },
    { key: 'nav.productPrompts', href: '/advertising-prompts' },
    { key: 'nav.productPhoenix', href: '/phoenix-lite-mentoring' },
    { key: 'nav.productTrafficPro', href: '/traffic-pro' },
    { key: 'nav.productTrafficMaster', href: '/traffic-master' },
    { key: 'nav.productWebDesign', href: '/web-design' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-white/85 backdrop-blur-xl shadow-[0_1px_3px_rgba(0,0,0,0.04),0_2px_12px_rgba(0,0,0,0.03)]'
            : 'bg-transparent'
        }`}
      >
        <div className="mx-auto flex items-center justify-between px-6 md:px-10 py-4 md:py-5">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="font-heading text-xl md:text-2xl font-bold tracking-tight text-gray-900">
              Santi<span className="text-primary-500">Ospina</span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/"
              className="text-sm font-medium text-gray-700 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap px-2"
            >
              {t('nav.home')}
            </Link>

            <span className="w-px h-5 bg-gray-200"></span>

            {productLinks.map((link) => (
              <Link
                key={link.key}
                to={link.href}
                className="text-xs font-medium text-gray-600 bg-gray-100 hover:bg-primary-50 hover:text-primary-600 border border-gray-200 hover:border-primary-200 px-3 py-1.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {t(link.key)}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <LanguageSwitcher />
            <Link
              to="https://wa.link/em25v1"
              className="px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap shadow-[0_2px_8px_rgba(200,30,30,0.25)] hover:shadow-[0_4px_16px_rgba(200,30,30,0.35)]"
            >
              {t('nav.bookCall')}
              <i className="ri-arrow-right-line ml-1.5"></i>
            </Link>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg text-gray-900 hover:bg-gray-100 transition-colors"
            aria-label={t('nav.toggleMenu')}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute top-0 right-0 w-72 h-full bg-white shadow-2xl p-6 pt-24 flex flex-col gap-3 overflow-y-auto">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="text-gray-800 text-base font-medium py-2 border-b border-gray-100 hover:text-primary-500 transition-colors"
            >
              {t('nav.home')}
            </Link>

            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-2 pb-1">
              {t('nav.productsTitle')}
            </div>

            {productLinks.map((link) => (
              <Link
                key={link.key}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className="text-gray-700 text-sm font-medium py-2 pl-3 border-l-2 border-gray-200 hover:border-primary-500 hover:text-primary-500 transition-all"
              >
                {t(link.key)}
              </Link>
            ))}

            <div className="mt-4 pt-4 border-t border-gray-100">
              <LanguageSwitcher />
            </div>
            <Link
              to="https://wa.link/em25v1"
              onClick={() => setMobileOpen(false)}
              className="mt-4 px-6 py-3 bg-primary-500 text-white text-center rounded-full font-semibold hover:bg-primary-600 transition-all shadow-[0_2px_8px_rgba(200,30,30,0.25)]"
            >
              {t('nav.bookCall')}
              <i className="ri-arrow-right-line ml-1.5"></i>
            </Link>
          </div>
        </div>
      )}
    </>
  );
}